import { useWindowSize } from "@react-hookz/web";
import { Drawer, Modal, Typography } from "@/components/atoms";

interface ResponsiveDialogProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: React.ReactNode;
  /**
   * Disable closing via backdrop click / Escape / swipe, leaving the rendered buttons
   * as the only exit. Required when the dialog hosts Stripe Elements: its hidden
   * controller iframes live outside the dialog panel, so headlessui's outside-click
   * detection dismisses the dialog on the submit click once the iframe held focus.
   */
  dismissible?: boolean;
  /**
   * Renders the action buttons. Mobile sheets stack full-width buttons (primary
   * first, text-style Cancel below); desktop modals show them side by side —
   * order/style accordingly using the isMobile flag.
   */
  renderButtons: (isMobile: boolean) => JSX.Element;
}

/**
 * Desktop (>=lg): centered Modal. Mobile: bottom sheet via the vaul Drawer atom,
 * matching the client portal mobile design (rounded-top sheet, stacked buttons).
 */
export const ResponsiveDialog = ({ open, title, description, onClose, children, dismissible = true, renderButtons }: ResponsiveDialogProps) => {
  const { width } = useWindowSize();

  if (width >= 1024) {
    return (
      <Modal
        open={open}
        onClose={dismissible ? onClose : () => undefined}
        variant="web-small"
        title={title}
        description={description}
        renderButtons={() => renderButtons(false)}
      >
        {children}
      </Modal>
    );
  }

  return (
    <Drawer open={open} dismissible={dismissible} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <span className="hidden" />
      <div className="flex flex-col gap-6 px-1 pb-6 pt-3">
        <div className="flex flex-col gap-2">
          <Typography variant="title">{title}</Typography>
          {description && (
            <Typography variant="paragraph2" className="text-neutral-dark-gray">
              {description}
            </Typography>
          )}
        </div>
        {children}
        <div className="flex flex-col gap-3">{renderButtons(true)}</div>
      </div>
    </Drawer>
  );
};
